import React, { useRef, useEffect, useState, useCallback } from 'react';
import { DrawingElement, Point, Tool } from '../types/whiteboard';
import '../styles/Whiteboard.css';

interface WhiteboardCanvasProps {
  elements: DrawingElement[];
  currentTool: Tool;
  currentColor: string;
  currentStrokeWidth: number;
  zoom: number;
  pan: Point;
  onAddElement: (element: DrawingElement) => void;
  onUpdateElement: (id: string, updates: Partial<DrawingElement>) => void;
  onPanChange: (pan: Point) => void;
  onRequestTextInput?: (point: Point) => void;
  onRequestStickyNote?: (point: Point) => void;
  onEditStickyNote?: (element: DrawingElement) => void;
  onSelectElement?: (element: DrawingElement | null) => void;
  onDragStateChange?: (isDragging: boolean) => void;
  onDeleteElement?: (id: string) => void;
}

const WhiteboardCanvas: React.FC<WhiteboardCanvasProps> = ({
  elements,
  currentTool,
  currentColor,
  currentStrokeWidth,
  zoom,
  pan,
  onAddElement,
  onUpdateElement,
  onPanChange,
  onRequestTextInput,
  onRequestStickyNote,
  onEditStickyNote,
  onSelectElement,
  onDragStateChange,
  onDeleteElement,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [currentElement, setCurrentElement] = useState<DrawingElement | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState<Point | null>(null);
  const [draggedElement, setDraggedElement] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<Point | null>(null);
  const [resizingElement, setResizingElement] = useState<string | null>(null);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [resizeStartPoint, setResizeStartPoint] = useState<Point | null>(null);
  const [originalSize, setOriginalSize] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [cursorStyle, setCursorStyle] = useState<string>('default');
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [hoveredConnectionPoint, setHoveredConnectionPoint] = useState<{ elementId: string; pointType: string } | null>(null);
  const [drawingConnection, setDrawingConnection] = useState(false);
  const [connectionStart, setConnectionStart] = useState<{ element: DrawingElement; point: Point; pointType: string } | null>(null);
  const [connectionPreview, setConnectionPreview] = useState<Point | null>(null);
  const [isErasing, setIsErasing] = useState(false);


  // Generate unique ID
  const generateId = () => `element-${Date.now()}-${Math.random()}`;

  // Simple real-time orthogonal routing for drawing connections
  const calculateSimpleOrthogonalPath = (
    start: Point,
    end: Point,
    startDirection: string,
    obstacles: DrawingElement[]
  ): Point[] => {
    const SPACING = 40; // Distance to maintain from start point
    const path: Point[] = [start];

    // Determine initial direction based on connection point
    let intermediateX = start.x;
    let intermediateY = start.y;

    if (startDirection === 'right') {
      intermediateX = start.x + SPACING;
    } else if (startDirection === 'left') {
      intermediateX = start.x - SPACING;
    } else if (startDirection === 'down') {
      intermediateY = start.y + SPACING;
    } else if (startDirection === 'up') {
      intermediateY = start.y - SPACING;
    }

    // Create simple L-shaped path
    if (startDirection === 'right' || startDirection === 'left') {
      // Horizontal first
      path.push({ x: intermediateX, y: start.y });
      path.push({ x: intermediateX, y: end.y });
    } else {
      // Vertical first
      path.push({ x: start.x, y: intermediateY });
      path.push({ x: end.x, y: intermediateY });
    }

    path.push(end);
    return path;
  };

  // Get mouse position relative to canvas
  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - pan.x) / zoom,
      y: (e.clientY - rect.top - pan.y) / zoom,
    };
  };

  // Check if point is inside element
  const isPointInElement = (point: Point, element: DrawingElement): boolean => {
    if (element.type === 'text') {
      if (element.x !== undefined && element.y !== undefined && element.text) {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!ctx) return false;

        // Calculate text dimensions
        const fontSize = element.fontSize || 20;
        const fontFamily = element.fontFamily || 'Arial, sans-serif';
        const bold = element.bold || false;
        const italic = element.italic || false;
        const fontStyle = italic ? 'italic' : 'normal';
        const fontWeight = bold ? 'bold' : 'normal';
        ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;

        const metrics = ctx.measureText(element.text);
        const textWidth = metrics.width;
        const textHeight = fontSize * 1.4;

        // Add padding for easier selection
        const padding = 5;
        return (
          point.x >= element.x - padding &&
          point.x <= element.x + textWidth + padding &&
          point.y >= element.y - padding &&
          point.y <= element.y + textHeight + padding
        );
      }
      return false;
    }

    if (element.type === 'sticky') {
      if (element.x !== undefined && element.y !== undefined) {
        const width = element.width || 200;
        const height = element.height || 200;
        return (
          point.x >= element.x &&
          point.x <= element.x + width &&
          point.y >= element.y &&
          point.y <= element.y + height
        );
      }
    }

    if (element.type === 'comment') {
      if (element.x !== undefined && element.y !== undefined) {
        const commentSize = element.width || 32;
        const centerX = element.x + commentSize / 2;
        const centerY = element.y + commentSize / 2;
        const radius = commentSize / 2;
        const distance = Math.sqrt(Math.pow(point.x - centerX, 2) + Math.pow(point.y - centerY, 2));
        return distance <= radius;
      }
    }

    // Check if point is on a shape
    if (['rectangle', 'circle', 'ellipse', 'triangle', 'diamond', 'star', 'hexagon', 'frame'].includes(element.type)) {
      if (element.x !== undefined && element.y !== undefined &&
          element.width !== undefined && element.height !== undefined) {

        // For simplicity, use bounding box for all shapes
        // This makes selection easier and is consistent with resize handles
        return (
          point.x >= element.x &&
          point.x <= element.x + Math.abs(element.width) &&
          point.y >= element.y &&
          point.y <= element.y + Math.abs(element.height)
        );
      }
    }

    // Check if point is on an arrow
    if (element.type === 'arrow' && element.points && element.points.length === 2) {
      let start = element.points[0];
      let end = element.points[1];

      // Dynamically calculate arrow positions based on connected sticky notes
      if (element.startElementId && element.startConnectionPoint) {
        const startElement = elements.find(el => el.id === element.startElementId);
        if (startElement && startElement.type === 'sticky') {
          const connectionPoints = getConnectionPoints(startElement);
          if (connectionPoints[element.startConnectionPoint]) {
            start = connectionPoints[element.startConnectionPoint];
          }
        }
      }

      if (element.endElementId && element.endConnectionPoint) {
        const endElement = elements.find(el => el.id === element.endElementId);
        if (endElement && endElement.type === 'sticky') {
          const connectionPoints = getConnectionPoints(endElement);
          if (connectionPoints[element.endConnectionPoint]) {
            end = connectionPoints[element.endConnectionPoint];
          }
        }
      }

      const lineType = element.lineType || 'straight';
      const threshold = 12; // pixels from line for easier selection

      // For orthogonal arrows, check all waypoint segments
      if (lineType === 'orthogonal') {
        const otherStickyNotes = elements.filter(el =>
          el.type === 'sticky' &&
          el.id !== element.startElementId &&
          el.id !== element.endElementId
        );

        const destinationCard = elements.find(el => el.id === element.endElementId);

        const waypoints = calculateOrthogonalPath(
          start,
          end,
          element.startConnectionPoint,
          element.endConnectionPoint,
          otherStickyNotes,
          destinationCard
        );

        // Check each segment in the waypoint path
        for (let i = 0; i < waypoints.length - 1; i++) {
          const segStart = waypoints[i];
          const segEnd = waypoints[i + 1];

          const lineLength = Math.sqrt(Math.pow(segEnd.x - segStart.x, 2) + Math.pow(segEnd.y - segStart.y, 2));
          if (lineLength === 0) continue;

          const t = Math.max(0, Math.min(1, ((point.x - segStart.x) * (segEnd.x - segStart.x) + (point.y - segStart.y) * (segEnd.y - segStart.y)) / (lineLength * lineLength)));
          const projectionX = segStart.x + t * (segEnd.x - segStart.x);
          const projectionY = segStart.y + t * (segEnd.y - segStart.y);
          const distance = Math.sqrt(Math.pow(point.x - projectionX, 2) + Math.pow(point.y - projectionY, 2));

          if (distance < threshold) {
            return true;
          }
        }
        return false;
      }

      // For straight and curved arrows, check the direct line
      const lineLength = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));
      if (lineLength === 0) return false;

      const t = Math.max(0, Math.min(1, ((point.x - start.x) * (end.x - start.x) + (point.y - start.y) * (end.y - start.y)) / (lineLength * lineLength)));
      const projectionX = start.x + t * (end.x - start.x);
      const projectionY = start.y + t * (end.y - start.y);
      const distance = Math.sqrt(Math.pow(point.x - projectionX, 2) + Math.pow(point.y - projectionY, 2));

      // Arrow is clickable if within threshold pixels of the line
      return distance < threshold;
    }

    // Check if point is on a line
    if (element.type === 'line' && element.points && element.points.length === 2) {
      const start = element.points[0];
      const end = element.points[1];
      const threshold = 12; // pixels from line for easier selection

      const lineLength = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));
      if (lineLength === 0) return false;

      const t = Math.max(0, Math.min(1, ((point.x - start.x) * (end.x - start.x) + (point.y - start.y) * (end.y - start.y)) / (lineLength * lineLength)));
      const projectionX = start.x + t * (end.x - start.x);
      const projectionY = start.y + t * (end.y - start.y);
      const distance = Math.sqrt(Math.pow(point.x - projectionX, 2) + Math.pow(point.y - projectionY, 2));

      return distance < threshold;
    }

    // Check if point is on a pen stroke
    if (element.type === 'pen' && element.points && element.points.length > 1) {
      const threshold = 12; // pixels from stroke for easier selection

      // Check each segment of the pen stroke
      for (let i = 0; i < element.points.length - 1; i++) {
        const start = element.points[i];
        const end = element.points[i + 1];

        const lineLength = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));
        if (lineLength === 0) continue;

        const t = Math.max(0, Math.min(1, ((point.x - start.x) * (end.x - start.x) + (point.y - start.y) * (end.y - start.y)) / (lineLength * lineLength)));
        const projectionX = start.x + t * (end.x - start.x);
        const projectionY = start.y + t * (end.y - start.y);
        const distance = Math.sqrt(Math.pow(point.x - projectionX, 2) + Math.pow(point.y - projectionY, 2));

        if (distance < threshold) {
          return true;
        }
      }
      return false;
    }

    return false;
  };

  // Find element at point
  const findElementAtPoint = (point: Point): DrawingElement | null => {
    // Search in reverse order (top elements first)
    for (let i = elements.length - 1; i >= 0; i--) {
      if (isPointInElement(point, elements[i])) {
        return elements[i];
      }
    }
    return null;
  };

  // Check if point is on resize handle
  const getResizeHandle = (point: Point, element: DrawingElement): string | null => {
    // Support sticky notes and shapes
    const supportedTypes = ['sticky', 'rectangle', 'circle', 'ellipse', 'triangle', 'diamond', 'star', 'hexagon', 'frame'];
    if (!supportedTypes.includes(element.type)) return null;
    if (element.x === undefined || element.y === undefined ||
        element.width === undefined || element.height === undefined) return null;

    const handleSize = 8;
    const threshold = handleSize;

    // Check corners
    if (Math.abs(point.x - element.x) < threshold && Math.abs(point.y - element.y) < threshold) {
      return 'nw';
    }
    if (Math.abs(point.x - (element.x + element.width)) < threshold && Math.abs(point.y - element.y) < threshold) {
      return 'ne';
    }
    if (Math.abs(point.x - element.x) < threshold && Math.abs(point.y - (element.y + element.height)) < threshold) {
      return 'sw';
    }
    if (Math.abs(point.x - (element.x + element.width)) < threshold && Math.abs(point.y - (element.y + element.height)) < threshold) {
      return 'se';
    }

    // Check edges
    if (Math.abs(point.y - element.y) < threshold && point.x > element.x && point.x < element.x + element.width) {
      return 'n';
    }
    if (Math.abs(point.y - (element.y + element.height)) < threshold && point.x > element.x && point.x < element.x + element.width) {
      return 's';
    }
    if (Math.abs(point.x - element.x) < threshold && point.y > element.y && point.y < element.y + element.height) {
      return 'w';
    }
    if (Math.abs(point.x - (element.x + element.width)) < threshold && point.y > element.y && point.y < element.y + element.height) {
      return 'e';
    }

    return null;
  };

  // Get cursor style based on resize handle
  const getCursorForHandle = (handle: string | null): string => {
    if (!handle) return 'default';
    const cursorMap: { [key: string]: string } = {
      'nw': 'nw-resize',
      'ne': 'ne-resize',
      'sw': 'sw-resize',
      'se': 'se-resize',
      'n': 'n-resize',
      's': 's-resize',
      'w': 'w-resize',
      'e': 'e-resize',
    };
    return cursorMap[handle] || 'default';
  };

  // Get connection points for a sticky note (positioned OUTSIDE like Miro/FigJam)
  const getConnectionPoints = (element: DrawingElement): { [key: string]: Point } => {
    if (element.type !== 'sticky' || !element.x || !element.y || !element.width || !element.height) {
      return {};
    }

    const offset = 20; // Position dots 20px outside the sticky note boundary (increased from 10px)

    return {
      'top': { x: element.x + element.width / 2, y: element.y - offset },
      'right': { x: element.x + element.width + offset, y: element.y + element.height / 2 },
      'bottom': { x: element.x + element.width / 2, y: element.y + element.height + offset },
      'left': { x: element.x - offset, y: element.y + element.height / 2 },
    };
  };

  // Check if point is on a connection point
  const getConnectionPoint = (point: Point, element: DrawingElement): string | null => {
    if (element.type !== 'sticky') return null;

    const connectionPoints = getConnectionPoints(element);
    const threshold = 10;

    for (const [key, cp] of Object.entries(connectionPoints)) {
      const distance = Math.sqrt(Math.pow(point.x - cp.x, 2) + Math.pow(point.y - cp.y, 2));
      if (distance < threshold) {
        return key;
      }
    }

    return null;
  };

  // Draw all elements on canvas
  const drawElements = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Apply transformations
    ctx.save();
    ctx.translate(pan.x, pan.y);
    ctx.scale(zoom, zoom);

    // Draw grid
    drawGrid(ctx, canvas.width, canvas.height);

    // Draw frames first (background layer)
    elements.filter(el => el.type === 'frame').forEach((element) => {
      drawElement(ctx, element);
    });

    // Draw all other elements on top
    elements.filter(el => el.type !== 'frame').forEach((element) => {
      drawElement(ctx, element);
    });

    // Draw current element being created
    if (currentElement) {
      drawElement(ctx, currentElement);
    }

    // Draw connection points for selected sticky notes or all sticky notes when drawing connection
    if (drawingConnection && connectionStart) {
      // When drawing connection, show dots on ALL sticky notes except the source
      elements.forEach(element => {
        if (element.type === 'sticky' && element.id !== connectionStart.element.id) {
          const connectionPoints = getConnectionPoints(element);
          Object.entries(connectionPoints).forEach(([key, point]) => {
            const isHovered = hoveredConnectionPoint?.elementId === element.id && hoveredConnectionPoint?.pointType === key;

            // Draw larger, more visible connection point dots
            ctx.beginPath();
            ctx.arc(point.x, point.y, isHovered ? 10 : 6, 0, 2 * Math.PI);
            ctx.fillStyle = isHovered ? '#43e97b' : '#ffffff';
            ctx.fill();
            ctx.strokeStyle = isHovered ? '#43e97b' : '#667eea';
            ctx.lineWidth = 2;
            ctx.stroke();
          });
        }
      });
    } else {
      // Show connection points only on selected sticky note when not drawing
      const selectedStickyNote = elements.find(el => el.id === selectedElement);
      if (selectedStickyNote && selectedStickyNote.type === 'sticky') {
        const connectionPoints = getConnectionPoints(selectedStickyNote);
        Object.entries(connectionPoints).forEach(([key, point]) => {
          const isHovered = hoveredConnectionPoint?.elementId === selectedStickyNote.id && hoveredConnectionPoint?.pointType === key;

          // Draw larger, more clickable connection point dots (like Miro/FigJam)
          ctx.beginPath();
          ctx.arc(point.x, point.y, isHovered ? 8 : 6, 0, 2 * Math.PI);
          ctx.fillStyle = isHovered ? '#667eea' : '#ffffff';
          ctx.fill();
          ctx.strokeStyle = '#667eea';
          ctx.lineWidth = 2;
          ctx.stroke();
        });
      }
    }

    // Draw connection preview while drawing
    if (drawingConnection && connectionStart && connectionPreview) {
      // Calculate orthogonal path
      const startDir = connectionStart.pointType === 'top' ? 'up' :
                       connectionStart.pointType === 'bottom' ? 'down' :
                       connectionStart.pointType === 'left' ? 'left' : 'right';

      const previewPath = calculateSimpleOrthogonalPath(
        connectionStart.point,
        connectionPreview,
        startDir,
        []
      );

      // Draw orthogonal path
      ctx.beginPath();
      ctx.moveTo(previewPath[0].x, previewPath[0].y);
      for (let i = 1; i < previewPath.length; i++) {
        ctx.lineTo(previewPath[i].x, previewPath[i].y);
      }
      ctx.strokeStyle = '#667eea';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw arrowhead at preview point
      const lastPoint = previewPath[previewPath.length - 1];
      const secondLastPoint = previewPath[previewPath.length - 2];
      const angle = Math.atan2(lastPoint.y - secondLastPoint.y, lastPoint.x - secondLastPoint.x);
      const arrowLength = 12;
      const arrowAngle = Math.PI / 6;

      ctx.beginPath();
      ctx.moveTo(connectionPreview.x, connectionPreview.y);
      ctx.lineTo(
        connectionPreview.x - arrowLength * Math.cos(angle - arrowAngle),
        connectionPreview.y - arrowLength * Math.sin(angle - arrowAngle)
      );
      ctx.moveTo(connectionPreview.x, connectionPreview.y);
      ctx.lineTo(
        connectionPreview.x - arrowLength * Math.cos(angle + arrowAngle),
        connectionPreview.y - arrowLength * Math.sin(angle + arrowAngle)
      );
      ctx.stroke();
    }

    ctx.restore();
  }, [elements, currentElement, zoom, pan, selectedElement, hoveredConnectionPoint, drawingConnection, connectionStart, connectionPreview]);

  // Draw grid background
  const drawGrid = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    const gridSize = 20;
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 0.5;

    const startX = Math.floor(-pan.x / zoom / gridSize) * gridSize;
    const startY = Math.floor(-pan.y / zoom / gridSize) * gridSize;
    const endX = startX + width / zoom + gridSize;
    const endY = startY + height / zoom + gridSize;

    for (let x = startX; x < endX; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, startY);
      ctx.lineTo(x, endY);
      ctx.stroke();
    }

    for (let y = startY; y < endY; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(startX, y);
      ctx.lineTo(endX, y);
      ctx.stroke();
    }
  };

  // Helper function to check if a line segment intersects with a rectangle
  const lineIntersectsRect = (
    lineStart: Point,
    lineEnd: Point,
    rect: { x: number; y: number; width: number; height: number },
    margin: number = 10
  ): boolean => {
    const rectLeft = rect.x - margin;
    const rectRight = rect.x + rect.width + margin;
    const rectTop = rect.y - margin;
    const rectBottom = rect.y + rect.height + margin;

    // Check if line is horizontal
    if (Math.abs(lineStart.y - lineEnd.y) < 1) {
      const y = lineStart.y;
      const minX = Math.min(lineStart.x, lineEnd.x);
      const maxX = Math.max(lineStart.x, lineEnd.x);

      // Check if horizontal line passes through rect vertically and overlaps horizontally
      if (y > rectTop && y < rectBottom && maxX > rectLeft && minX < rectRight) {
        return true;
      }
    }

    // Check if line is vertical
    if (Math.abs(lineStart.x - lineEnd.x) < 1) {
      const x = lineStart.x;
      const minY = Math.min(lineStart.y, lineEnd.y);
      const maxY = Math.max(lineStart.y, lineEnd.y);

      // Check if vertical line passes through rect horizontally and overlaps vertically
      if (x > rectLeft && x < rectRight && maxY > rectTop && minY < rectBottom) {
        return true;
      }
    }

    return false;
  };

  // Calculate orthogonal routing waypoints that avoid sticky notes
  const calculateOrthogonalPath = (
    start: Point,
    end: Point,
    startPoint: string | undefined,
    endPoint: string | undefined,
    obstacles: DrawingElement[],
    destinationCard?: DrawingElement
  ): Point[] => {
    const SPACING = 40; // Distance from connection point
    const MARGIN = 20; // Extra margin around obstacles

    // Include destination card as an obstacle
    const allObstacles = destinationCard
      ? [...obstacles, destinationCard]
      : obstacles;

    // Determine start and end directions
    const startDir = startPoint === 'top' ? 'up' :
                     startPoint === 'bottom' ? 'down' :
                     startPoint === 'left' ? 'left' : 'right';

    const endDir = endPoint === 'top' ? 'up' :
                   endPoint === 'bottom' ? 'down' :
                   endPoint === 'left' ? 'left' : 'right';

    // Calculate first waypoint - move out from start connection point
    let p1: Point = { ...start };
    if (startDir === 'up') p1.y -= SPACING;
    else if (startDir === 'down') p1.y += SPACING;
    else if (startDir === 'left') p1.x -= SPACING;
    else p1.x += SPACING;

    // Calculate last waypoint - approach end connection point
    let p2: Point = { ...end };
    if (endDir === 'up') p2.y -= SPACING;
    else if (endDir === 'down') p2.y += SPACING;
    else if (endDir === 'left') p2.x -= SPACING;
    else p2.x += SPACING;

    // Check if directions are opposite
    const isOppositeHorizontal = (startDir === 'left' && endDir === 'right') || (startDir === 'right' && endDir === 'left');
    const isOppositeVertical = (startDir === 'up' && endDir === 'down') || (startDir === 'down' && endDir === 'up');
    const isSameHorizontal = (startDir === 'left' && endDir === 'left') || (startDir === 'right' && endDir === 'right');
    const isSameVertical = (startDir === 'up' && endDir === 'up') || (startDir === 'down' && endDir === 'down');
    const isHorizontalFirst = (startDir === 'left' || startDir === 'right');

    let path: Point[] = [start];

    // Helper to find clear X position that avoids obstacles
    const findClearX = (baseX: number, y1: number, y2: number, preferRight: boolean): number => {
      let testX = baseX;
      const minY = Math.min(y1, y2);
      const maxY = Math.max(y1, y2);

      for (const obs of allObstacles) {
        if (obs.type === 'sticky' && obs.x !== undefined && obs.y !== undefined &&
            obs.width !== undefined && obs.height !== undefined) {

          const testLine = { x: testX, y: minY };
          const testLineEnd = { x: testX, y: maxY };
          const rect = { x: obs.x, y: obs.y, width: obs.width, height: obs.height };

          if (lineIntersectsRect(testLine, testLineEnd, rect, MARGIN)) {
            testX = preferRight
              ? obs.x + obs.width + SPACING + MARGIN
              : obs.x - SPACING - MARGIN;
          }
        }
      }
      return testX;
    };

    // Helper to find clear Y position that avoids obstacles
    const findClearY = (baseY: number, x1: number, x2: number, preferDown: boolean): number => {
      let testY = baseY;
      const minX = Math.min(x1, x2);
      const maxX = Math.max(x1, x2);

      for (const obs of allObstacles) {
        if (obs.type === 'sticky' && obs.x !== undefined && obs.y !== undefined &&
            obs.width !== undefined && obs.height !== undefined) {

          const testLine = { x: minX, y: testY };
          const testLineEnd = { x: maxX, y: testY };
          const rect = { x: obs.x, y: obs.y, width: obs.width, height: obs.height };

          if (lineIntersectsRect(testLine, testLineEnd, rect, MARGIN)) {
            testY = preferDown
              ? obs.y + obs.height + SPACING + MARGIN
              : obs.y - SPACING - MARGIN;
          }
        }
      }
      return testY;
    };

    // Build path based on connection type
    if (isOppositeHorizontal) {
      // Right-to-left or left-to-right
      let midX = (p1.x + p2.x) / 2;

      // Check if vertical line at midX conflicts with obstacles
      midX = findClearX(midX, start.y, end.y, startDir === 'right');

      path.push({ x: p1.x, y: start.y });
      path.push({ x: midX, y: start.y });
      path.push({ x: midX, y: end.y });
      path.push({ x: p2.x, y: end.y });
    } else if (isOppositeVertical) {
      // Up-to-down or down-to-up
      let midY = (p1.y + p2.y) / 2;

      // Check if horizontal line at midY conflicts with obstacles
      midY = findClearY(midY, start.x, end.x, startDir === 'down');

      path.push({ x: start.x, y: p1.y });
      path.push({ x: start.x, y: midY });
      path.push({ x: end.x, y: midY });
      path.push({ x: end.x, y: p2.y });
    } else if (isSameHorizontal) {
      // Both pointing left or both pointing right
      let extendX = startDir === 'right' ? Math.max(p1.x, p2.x) : Math.min(p1.x, p2.x);

      // Check if vertical line at extendX conflicts with obstacles
      extendX = findClearX(extendX, start.y, end.y, startDir === 'right');

      path.push({ x: extendX, y: start.y });
      path.push({ x: extendX, y: end.y });
      if (Math.abs(extendX - p2.x) > 5) {
        path.push({ x: p2.x, y: end.y });
      }
    } else if (isSameVertical) {
      // Both pointing up or both pointing down
      let extendY = startDir === 'down' ? Math.max(p1.y, p2.y) : Math.min(p1.y, p2.y);

      // Check if horizontal line at extendY conflicts with obstacles
      extendY = findClearY(extendY, start.x, end.x, startDir === 'down');

      path.push({ x: start.x, y: extendY });
      path.push({ x: end.x, y: extendY });
      if (Math.abs(extendY - p2.y) > 5) {
        path.push({ x: end.x, y: p2.y });
      }
    } else if (isHorizontalFirst) {
      // Horizontal then vertical (perpendicular connections)
      path.push({ x: p1.x, y: start.y });

      // Check if there's an obstacle in the way
      let hasObstacle = false;
      for (const obs of allObstacles) {
        if (obs.type === 'sticky' && obs.x !== undefined && obs.y !== undefined &&
            obs.width !== undefined && obs.height !== undefined) {
          const rect = { x: obs.x, y: obs.y, width: obs.width, height: obs.height };
          if (lineIntersectsRect({ x: p1.x, y: start.y }, { x: p1.x, y: p2.y }, rect, MARGIN)) {
            hasObstacle = true;
            break;
          }
        }
      }

      if (hasObstacle) {
        const clearX = findClearX(p1.x, start.y, p2.y, startDir === 'right');
        path.push({ x: clearX, y: start.y });
        path.push({ x: clearX, y: p2.y });
      } else {
        path.push({ x: p1.x, y: p2.y });
      }

      if (Math.abs(p1.x - p2.x) > 5) {
        path.push({ x: p2.x, y: p2.y });
      }
    } else {
      // Vertical then horizontal (perpendicular connections)
      path.push({ x: start.x, y: p1.y });

      // Check if there's an obstacle in the way
      let hasObstacle = false;
      for (const obs of allObstacles) {
        if (obs.type === 'sticky' && obs.x !== undefined && obs.y !== undefined &&
            obs.width !== undefined && obs.height !== undefined) {
          const rect = { x: obs.x, y: obs.y, width: obs.width, height: obs.height };
          if (lineIntersectsRect({ x: start.x, y: p1.y }, { x: p2.x, y: p1.y }, rect, MARGIN)) {
            hasObstacle = true;
            break;
          }
        }
      }

      if (hasObstacle) {
        const clearY = findClearY(p1.y, start.x, p2.x, startDir === 'down');
        path.push({ x: start.x, y: clearY });
        path.push({ x: p2.x, y: clearY });
      } else {
        path.push({ x: p2.x, y: p1.y });
      }

      if (Math.abs(p1.y - p2.y) > 5) {
        path.push({ x: p2.x, y: p2.y });
      }
    }

    path.push(end);

    // Remove duplicate consecutive points
    const cleaned: Point[] = [path[0]];
    for (let i = 1; i < path.length; i++) {
      const prev = cleaned[cleaned.length - 1];
      if (Math.abs(path[i].x - prev.x) > 1 || Math.abs(path[i].y - prev.y) > 1) {
        cleaned.push(path[i]);
      }
    }

    return cleaned;
  };

  // Draw individual element
  const drawElement = (ctx: CanvasRenderingContext2D, element: DrawingElement) => {
    ctx.strokeStyle = element.color;
    ctx.fillStyle = element.backgroundColor || element.color;
    ctx.lineWidth = element.strokeWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    switch (element.type) {
      case 'pen':
        if (element.points && element.points.length > 1) {
          const isSelected = element.id === selectedElement;

          // Apply selection style
          ctx.strokeStyle = isSelected ? '#667eea' : element.color;
          ctx.lineWidth = isSelected ? (element.strokeWidth || 2) + 2 : (element.strokeWidth || 2);

          ctx.beginPath();
          ctx.moveTo(element.points[0].x, element.points[0].y);
          for (let i = 1; i < element.points.length; i++) {
            ctx.lineTo(element.points[i].x, element.points[i].y);
          }
          ctx.stroke();

          // Draw endpoint handles if selected
          if (isSelected) {
            const handleSize = 8;
            ctx.fillStyle = '#667eea';
            // Start point handle
            ctx.fillRect(
              element.points[0].x - handleSize / 2,
              element.points[0].y - handleSize / 2,
              handleSize,
              handleSize
            );
            // End point handle
            const lastPoint = element.points[element.points.length - 1];
            ctx.fillRect(
              lastPoint.x - handleSize / 2,
              lastPoint.y - handleSize / 2,
              handleSize,
              handleSize
            );
          }
        }
        break;

      case 'rectangle':
        if (element.x !== undefined && element.y !== undefined &&
            element.width !== undefined && element.height !== undefined) {
          // Fill if background color is set and not transparent
          if (element.backgroundColor && element.backgroundColor !== 'transparent') {
            ctx.fillStyle = element.backgroundColor;
            ctx.fillRect(element.x, element.y, element.width, element.height);
          }
          ctx.strokeRect(element.x, element.y, element.width, element.height);

          // Draw selection and resize handles if selected
          if (element.id === selectedElement) {
            ctx.strokeStyle = '#667eea';
            ctx.lineWidth = 3;
            ctx.strokeRect(element.x, element.y, element.width, element.height);

            // Draw resize handles
            const handleSize = 8;
            ctx.fillStyle = '#667eea';
            // Corners
            ctx.fillRect(element.x - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            // Edges
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
          }
        }
        break;

      case 'frame':
        if (element.x !== undefined && element.y !== undefined &&
            element.width !== undefined && element.height !== undefined) {

          // Draw subtle background with rounded corners
          const backgroundColor = element.backgroundColor || 'rgba(230, 247, 255, 0.3)';
          ctx.fillStyle = backgroundColor;
          ctx.fillRect(element.x, element.y, element.width, element.height);

          // Draw subtle border
          ctx.strokeStyle = element.color || 'rgba(102, 126, 234, 0.3)';
          ctx.lineWidth = 2;
          ctx.strokeRect(element.x, element.y, element.width, element.height);

          // Draw frame label in top-left corner
          if (element.text) {
            const labelPadding = 8;
            const labelHeight = 28;

            // Measure text width
            ctx.font = 'bold 13px Inter, system-ui, -apple-system, sans-serif';
            const textWidth = ctx.measureText(element.text).width;
            const labelWidth = textWidth + labelPadding * 2;

            // Draw label background
            ctx.fillStyle = element.color || '#667eea';
            ctx.fillRect(element.x + 12, element.y + 12, labelWidth, labelHeight);

            // Draw label text
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(element.text, element.x + 12 + labelPadding, element.y + 12 + labelHeight / 2);
          }

          // Draw selection and resize handles if selected
          if (element.id === selectedElement) {
            ctx.strokeStyle = '#667eea';
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 5]);
            ctx.strokeRect(element.x, element.y, element.width, element.height);
            ctx.setLineDash([]);

            // Draw resize handles
            const handleSize = 10;
            ctx.fillStyle = '#667eea';
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 2;

            // Helper function to draw handle
            const drawHandle = (x: number, y: number) => {
              ctx.beginPath();
              ctx.arc(x, y, handleSize / 2, 0, 2 * Math.PI);
              ctx.fill();
              ctx.stroke();
            };

            // Corners
            drawHandle(element.x, element.y);
            drawHandle(element.x + element.width, element.y);
            drawHandle(element.x, element.y + element.height);
            drawHandle(element.x + element.width, element.y + element.height);
            // Edges
            drawHandle(element.x + element.width / 2, element.y);
            drawHandle(element.x + element.width / 2, element.y + element.height);
            drawHandle(element.x, element.y + element.height / 2);
            drawHandle(element.x + element.width, element.y + element.height / 2);
          }
        }
        break;

      case 'circle':
        if (element.x !== undefined && element.y !== undefined && element.width !== undefined) {
          const radius = Math.abs(element.width) / 2;
          const centerX = element.x + element.width / 2;
          const centerY = element.y! + (element.height || element.width) / 2;
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
          // Fill if background color is set and not transparent
          if (element.backgroundColor && element.backgroundColor !== 'transparent') {
            ctx.fillStyle = element.backgroundColor;
            ctx.fill();
          }
          ctx.stroke();

          // Draw selection and resize handles if selected
          if (element.id === selectedElement && element.height !== undefined) {
            // Draw selection box around circle
            ctx.strokeStyle = '#667eea';
            ctx.lineWidth = 3;
            ctx.strokeRect(element.x, element.y, element.width, element.height);

            // Draw resize handles
            const handleSize = 8;
            ctx.fillStyle = '#667eea';
            // Corners
            ctx.fillRect(element.x - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            // Edges
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
          }
        }
        break;

      case 'ellipse':
        if (element.x !== undefined && element.y !== undefined &&
            element.width !== undefined && element.height !== undefined) {
          const centerX = element.x + element.width / 2;
          const centerY = element.y + element.height / 2;
          const radiusX = Math.abs(element.width) / 2;
          const radiusY = Math.abs(element.height) / 2;
          ctx.beginPath();
          ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
          // Fill if background color is set and not transparent
          if (element.backgroundColor && element.backgroundColor !== 'transparent') {
            ctx.fillStyle = element.backgroundColor;
            ctx.fill();
          }
          ctx.stroke();

          // Draw selection and resize handles if selected
          if (element.id === selectedElement) {
            // Draw selection box around ellipse
            ctx.strokeStyle = '#667eea';
            ctx.lineWidth = 3;
            ctx.strokeRect(element.x, element.y, element.width, element.height);

            // Draw resize handles
            const handleSize = 8;
            ctx.fillStyle = '#667eea';
            // Corners
            ctx.fillRect(element.x - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            // Edges
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
          }
        }
        break;

      case 'triangle':
        if (element.x !== undefined && element.y !== undefined &&
            element.width !== undefined && element.height !== undefined) {
          const x = element.x;
          const y = element.y;
          const w = element.width;
          const h = element.height;
          ctx.beginPath();
          ctx.moveTo(x + w / 2, y); // Top point
          ctx.lineTo(x + w, y + h); // Bottom right
          ctx.lineTo(x, y + h); // Bottom left
          ctx.closePath();
          // Fill if background color is set and not transparent
          if (element.backgroundColor && element.backgroundColor !== 'transparent') {
            ctx.fillStyle = element.backgroundColor;
            ctx.fill();
          }
          ctx.stroke();

          // Draw selection and resize handles if selected
          if (element.id === selectedElement) {
            ctx.strokeStyle = '#667eea';
            ctx.lineWidth = 3;
            ctx.strokeRect(element.x, element.y, element.width, element.height);

            const handleSize = 8;
            ctx.fillStyle = '#667eea';
            ctx.fillRect(element.x - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
          }
        }
        break;

      case 'diamond':
        if (element.x !== undefined && element.y !== undefined &&
            element.width !== undefined && element.height !== undefined) {
          const x = element.x;
          const y = element.y;
          const w = element.width;
          const h = element.height;
          ctx.beginPath();
          ctx.moveTo(x + w / 2, y); // Top point
          ctx.lineTo(x + w, y + h / 2); // Right point
          ctx.lineTo(x + w / 2, y + h); // Bottom point
          ctx.lineTo(x, y + h / 2); // Left point
          ctx.closePath();
          // Fill if background color is set and not transparent
          if (element.backgroundColor && element.backgroundColor !== 'transparent') {
            ctx.fillStyle = element.backgroundColor;
            ctx.fill();
          }
          ctx.stroke();

          // Draw selection and resize handles if selected
          if (element.id === selectedElement) {
            ctx.strokeStyle = '#667eea';
            ctx.lineWidth = 3;
            ctx.strokeRect(element.x, element.y, element.width, element.height);

            const handleSize = 8;
            ctx.fillStyle = '#667eea';
            ctx.fillRect(element.x - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
          }
        }
        break;

      case 'star':
        if (element.x !== undefined && element.y !== undefined &&
            element.width !== undefined && element.height !== undefined) {
          const centerX = element.x + element.width / 2;
          const centerY = element.y + element.height / 2;
          const outerRadius = Math.min(Math.abs(element.width), Math.abs(element.height)) / 2;
          const innerRadius = outerRadius * 0.4;
          const points = 5;

          ctx.beginPath();
          for (let i = 0; i < points * 2; i++) {
            const radius = i % 2 === 0 ? outerRadius : innerRadius;
            const angle = (i * Math.PI) / points - Math.PI / 2;
            const x = centerX + radius * Math.cos(angle);
            const y = centerY + radius * Math.sin(angle);
            if (i === 0) {
              ctx.moveTo(x, y);
            } else {
              ctx.lineTo(x, y);
            }
          }
          ctx.closePath();
          // Fill if background color is set and not transparent
          if (element.backgroundColor && element.backgroundColor !== 'transparent') {
            ctx.fillStyle = element.backgroundColor;
            ctx.fill();
          }
          ctx.stroke();

          // Draw selection and resize handles if selected
          if (element.id === selectedElement) {
            ctx.strokeStyle = '#667eea';
            ctx.lineWidth = 3;
            ctx.strokeRect(element.x, element.y, element.width, element.height);

            const handleSize = 8;
            ctx.fillStyle = '#667eea';
            ctx.fillRect(element.x - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
          }
        }
        break;

      case 'hexagon':
        if (element.x !== undefined && element.y !== undefined &&
            element.width !== undefined && element.height !== undefined) {
          const centerX = element.x + element.width / 2;
          const centerY = element.y + element.height / 2;
          const radius = Math.min(Math.abs(element.width), Math.abs(element.height)) / 2;
          const sides = 6;

          ctx.beginPath();
          for (let i = 0; i < sides; i++) {
            const angle = (i * 2 * Math.PI) / sides - Math.PI / 2;
            const x = centerX + radius * Math.cos(angle);
            const y = centerY + radius * Math.sin(angle);
            if (i === 0) {
              ctx.moveTo(x, y);
            } else {
              ctx.lineTo(x, y);
            }
          }
          ctx.closePath();
          // Fill if background color is set and not transparent
          if (element.backgroundColor && element.backgroundColor !== 'transparent') {
            ctx.fillStyle = element.backgroundColor;
            ctx.fill();
          }
          ctx.stroke();

          // Draw selection and resize handles if selected
          if (element.id === selectedElement) {
            ctx.strokeStyle = '#667eea';
            ctx.lineWidth = 3;
            ctx.strokeRect(element.x, element.y, element.width, element.height);

            const handleSize = 8;
            ctx.fillStyle = '#667eea';
            ctx.fillRect(element.x - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
          }
        }
        break;

      case 'line':
        if (element.points && element.points.length === 2) {
          const isSelected = element.id === selectedElement;

          // Save context for rotation
          ctx.save();

          // Calculate center point for rotation
          const centerX = (element.points[0].x + element.points[1].x) / 2;
          const centerY = (element.points[0].y + element.points[1].y) / 2;

          // Apply rotation if specified
          if (element.rotation) {
            ctx.translate(centerX, centerY);
            ctx.rotate((element.rotation * Math.PI) / 180);
            ctx.translate(-centerX, -centerY);
          }

          // Apply selection style
          ctx.strokeStyle = isSelected ? '#667eea' : element.color;
          ctx.lineWidth = isSelected ? (element.strokeWidth || 2) + 2 : (element.strokeWidth || 2);

          // Apply line pattern
          const linePattern = element.linePattern || 'solid';
          switch (linePattern) {
            case 'thin-dotted':
              ctx.setLineDash([2, 4]);
              break;
            case 'thick-dotted':
              ctx.setLineDash([8, 8]);
              break;
            case 'solid':
            default:
              ctx.setLineDash([]);
              break;
          }

          ctx.beginPath();
          ctx.moveTo(element.points[0].x, element.points[0].y);
          ctx.lineTo(element.points[1].x, element.points[1].y);
          ctx.stroke();

          // Reset line dash
          ctx.setLineDash([]);

          // Draw endpoint handles if selected
          if (isSelected) {
            const handleSize = 8;
            ctx.fillStyle = '#667eea';
            // Start point handle
            ctx.fillRect(
              element.points[0].x - handleSize / 2,
              element.points[0].y - handleSize / 2,
              handleSize,
              handleSize
            );
            // End point handle
            ctx.fillRect(
              element.points[1].x - handleSize / 2,
              element.points[1].y - handleSize / 2,
              handleSize,
              handleSize
            );
          }

          // Restore context
          ctx.restore();
        }
        break;

      case 'arrow':
        if (element.points && element.points.length === 2) {
          let start = element.points[0];
          let end = element.points[1];

          // Dynamically calculate arrow positions based on connected sticky notes
          // Use the STORED connection point type to keep arrows on the same side
          if (element.startElementId && element.startConnectionPoint) {
            const startElement = elements.find(el => el.id === element.startElementId);
            if (startElement && startElement.type === 'sticky') {
              const connectionPoints = getConnectionPoints(startElement);
              // Use the specific stored connection point (top/right/bottom/left)
              if (connectionPoints[element.startConnectionPoint]) {
                start = connectionPoints[element.startConnectionPoint];
              }
            }
          }

          if (element.endElementId && element.endConnectionPoint) {
            const endElement = elements.find(el => el.id === element.endElementId);
            if (endElement && endElement.type === 'sticky') {
              const connectionPoints = getConnectionPoints(endElement);
              // Use the specific stored connection point (top/right/bottom/left)
              if (connectionPoints[element.endConnectionPoint]) {
                end = connectionPoints[element.endConnectionPoint];
              }
            }
          }

          const isSelected = element.id === selectedElement;
          const lineType = element.lineType || 'straight';
          const linePattern = element.linePattern || 'solid';
          const arrowEnd = element.arrowEnd || 'arrow';

          // Apply line pattern
          ctx.strokeStyle = isSelected ? '#667eea' : element.color;
          ctx.lineWidth = isSelected ? (element.strokeWidth || 2) + 1 : (element.strokeWidth || 2);

          if (linePattern === 'thin-dotted') {
            ctx.setLineDash([2, 4]);
          } else if (linePattern === 'thick-dotted') {
            ctx.setLineDash([8, 8]);
          } else {
            ctx.setLineDash([]);
          }

          // Draw line based on line type
          ctx.beginPath();
          if (lineType === 'straight') {
            ctx.moveTo(start.x, start.y);
            ctx.lineTo(end.x, end.y);
          } else if (lineType === 'curved') {
            // Draw a smooth curved line
            const midX = (start.x + end.x) / 2;
            const midY = (start.y + end.y) / 2;
            const dx = end.x - start.x;
            const dy = end.y - start.y;
            // Control point offset perpendicular to the line
            const offsetX = -dy * 0.2;
            const offsetY = dx * 0.2;
            ctx.moveTo(start.x, start.y);
            ctx.quadraticCurveTo(midX + offsetX, midY + offsetY, end.x, end.y);
          } else if (lineType === 'orthogonal') {
            // Draw smart orthogonal routing that avoids sticky notes
            const otherStickyNotes = elements.filter(el =>
              el.type === 'sticky' &&
              el.id !== element.startElementId &&
              el.id !== element.endElementId
            );

            // Get destination card for special handling
            const destinationCard = elements.find(el => el.id === element.endElementId);

            const waypoints = calculateOrthogonalPath(
              start,
              end,
              element.startConnectionPoint,
              element.endConnectionPoint,
              otherStickyNotes,
              destinationCard
            );

            // Draw path through waypoints
            ctx.moveTo(waypoints[0].x, waypoints[0].y);
            for (let i = 1; i < waypoints.length; i++) {
              ctx.lineTo(waypoints[i].x, waypoints[i].y);
            }
          }
          ctx.stroke();

          // Reset line dash after drawing
          ctx.setLineDash([]);

          // Draw arrowhead at the end if arrowEnd is 'arrow'
          if (arrowEnd === 'arrow') {
            let angle: number;
            if (lineType === 'orthogonal') {
              // For orthogonal, calculate angle from the last segment
              const otherStickyNotes = elements.filter(el =>
                el.type === 'sticky' &&
                el.id !== element.startElementId &&
                el.id !== element.endElementId
              );

              const destinationCard = elements.find(el => el.id === element.endElementId);

              const waypoints = calculateOrthogonalPath(
                start,
                end,
                element.startConnectionPoint,
                element.endConnectionPoint,
                otherStickyNotes,
                destinationCard
              );

              // Calculate angle from last two waypoints
              const lastWaypoint = waypoints[waypoints.length - 1];
              const secondLastWaypoint = waypoints[waypoints.length - 2];
              angle = Math.atan2(
                lastWaypoint.y - secondLastWaypoint.y,
                lastWaypoint.x - secondLastWaypoint.x
              );
            } else if (lineType === 'curved') {
              // For curved, calculate tangent at the end point
              const midX = (start.x + end.x) / 2;
              const midY = (start.y + end.y) / 2;
              const dx = end.x - start.x;
              const dy = end.y - start.y;
              const offsetX = -dy * 0.2;
              const offsetY = dx * 0.2;
              // Tangent at end of quadratic curve
              angle = Math.atan2(end.y - (midY + offsetY), end.x - (midX + offsetX));
            } else {
              // Straight line
              angle = Math.atan2(end.y - start.y, end.x - start.x);
            }

            const arrowLength = 12;
            const arrowAngle = Math.PI / 6; // 30 degrees

            ctx.fillStyle = isSelected ? '#667eea' : element.color;
            ctx.beginPath();
            ctx.moveTo(end.x, end.y);
            ctx.lineTo(
              end.x - arrowLength * Math.cos(angle - arrowAngle),
              end.y - arrowLength * Math.sin(angle - arrowAngle)
            );
            ctx.lineTo(
              end.x - arrowLength * Math.cos(angle + arrowAngle),
              end.y - arrowLength * Math.sin(angle + arrowAngle)
            );
            ctx.closePath();
            ctx.fill();
          }

          // Draw arrow text if present
          if (element.arrowText) {
            const midX = (start.x + end.x) / 2;
            const midY = (start.y + end.y) / 2;

            // Draw text background
            ctx.font = '14px Arial';
            const textMetrics = ctx.measureText(element.arrowText);
            const textWidth = textMetrics.width;
            const textHeight = 18;
            const padding = 4;

            ctx.fillStyle = 'white';
            ctx.fillRect(midX - textWidth / 2 - padding, midY - textHeight / 2 - padding, textWidth + padding * 2, textHeight + padding * 2);

            // Draw text border
            ctx.strokeStyle = isSelected ? '#667eea' : element.color;
            ctx.lineWidth = 1;
            ctx.strokeRect(midX - textWidth / 2 - padding, midY - textHeight / 2 - padding, textWidth + padding * 2, textHeight + padding * 2);

            // Draw text
            ctx.fillStyle = element.color;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(element.arrowText, midX, midY);
          }

          // Draw endpoint handles if selected
          if (isSelected) {
            const handleSize = 8;
            ctx.fillStyle = '#667eea';
            // Start point handle
            ctx.fillRect(
              start.x - handleSize / 2,
              start.y - handleSize / 2,
              handleSize,
              handleSize
            );
            // End point handle
            ctx.fillRect(
              end.x - handleSize / 2,
              end.y - handleSize / 2,
              handleSize,
              handleSize
            );
          }
        }
        break;

      case 'sticky':
        if (element.x !== undefined && element.y !== undefined &&
            element.width !== undefined && element.height !== undefined) {
          // Draw shadow (Miro-like)
          ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
          ctx.shadowBlur = 8;
          ctx.shadowOffsetX = 2;
          ctx.shadowOffsetY = 2;

          // Draw sticky note background
          ctx.fillStyle = element.backgroundColor || '#fff475';
          ctx.fillRect(element.x, element.y, element.width, element.height);

          // Reset shadow for border and text
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;

          // Draw border
          const isSelected = element.id === selectedElement;
          ctx.strokeStyle = isSelected ? '#667eea' : 'rgba(0, 0, 0, 0.1)';
          ctx.lineWidth = isSelected ? 3 : 1;
          ctx.strokeRect(element.x, element.y, element.width, element.height);

          // Draw text
          if (element.text) {
            const fontSize = element.fontSize || 16;
            const fontFamily = element.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial';
            const textColor = element.textColor || 'rgba(0, 0, 0, 0.8)';
            const textAlign = element.textAlign || 'left';
            const bold = element.bold || false;
            const italic = element.italic || false;
            const underline = element.underline || false;
            const strikethrough = element.strikethrough || false;

            const padding = 12;

            // Helper function to truncate text if too long
            const truncateText = (text: string, maxWidth: number, font: string): string => {
              ctx.font = font;
              let truncated = text;
              while (ctx.measureText(truncated).width > maxWidth && truncated.length > 0) {
                truncated = truncated.slice(0, -1);
              }
              return truncated.length < text.length ? truncated + '...' : text;
            };

            // Draw tag at top
            let topReservedSpace = 0;
            if (element.tag) {
              ctx.font = `${10}px ${fontFamily}`;
              ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
              const topY = element.y + padding;
              const availableWidth = element.width - padding * 2;
              topReservedSpace = 20; // Reserve space at top

              ctx.textAlign = 'left';
              const tagText = `#${truncateText(element.tag, availableWidth, ctx.font)}`;
              ctx.fillText(tagText, element.x + padding, topY);
            }

            // Draw author at bottom
            let bottomReservedSpace = 0;
            if (element.author) {
              ctx.font = `${10}px ${fontFamily}`;
              ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
              const availableWidth = element.width - padding * 2;
              const bottomY = element.y + element.height - padding - 10;
              bottomReservedSpace = 20; // Reserve space at bottom

              ctx.textAlign = 'right';
              const authorText = `@${truncateText(element.author, availableWidth, ctx.font)}`;
              ctx.fillText(authorText, element.x + element.width - padding, bottomY);
            }

            ctx.fillStyle = textColor;
            // Build font string with bold and italic
            const fontStyle = italic ? 'italic' : 'normal';
            const fontWeight = bold ? 'bold' : 'normal';
            ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
            ctx.textAlign = textAlign;
            ctx.textBaseline = 'top';

            // Calculate text area between tag and author
            const textStartY = element.y + padding + topReservedSpace;
            const textAreaHeight = element.height - padding * 2 - topReservedSpace - bottomReservedSpace;

            // Check if text contains HTML formatting
            const containsHTML = /<[^>]*>/g.test(element.text);

            if (containsHTML) {
              // Parse and render formatted HTML text
              const segments = parseHTMLText(element.text);
              renderFormattedText(
                ctx, segments, element.x + padding, textStartY,
                element.width - padding * 2, fontSize * 1.4, fontSize, fontFamily, textColor, textAlign, textAreaHeight
              );
            } else {
              // Render plain text with global formatting
              wrapText(ctx, element.text, element.x + padding, textStartY,
                       element.width - padding * 2, fontSize * 1.4, textAlign, textAreaHeight, underline, strikethrough);
            }
          }

          // Draw resize handles when selected
          if (isSelected) {
            const handleSize = 8;
            ctx.fillStyle = '#667eea';

            // Corner handles
            ctx.fillRect(element.x - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);

            // Edge handles
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width / 2 - handleSize / 2, element.y + element.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + element.width - handleSize / 2, element.y + element.height / 2 - handleSize / 2, handleSize, handleSize);
          }
        }
        break;

      case 'text':
        if (element.x !== undefined && element.y !== undefined && element.text) {
          const fontSize = element.fontSize || 20;
          const fontFamily = element.fontFamily || 'Arial, sans-serif';
          const textColor = element.textColor || element.color;
          const bold = element.bold || false;
          const italic = element.italic || false;
          const underline = element.underline || false;
          const strikethrough = element.strikethrough || false;
          const textAlign = element.textAlign || 'left';

          // Build font string
          const fontStyle = italic ? 'italic' : 'normal';
          const fontWeight = bold ? 'bold' : 'normal';
          ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;

          const metrics = ctx.measureText(element.text);
          const textWidth = metrics.width;
          const textHeight = fontSize * 1.4;
          const isTextSelected = element.id === selectedElement;

          if (isTextSelected) {
            // Draw selection background
            ctx.fillStyle = 'rgba(102, 126, 234, 0.1)';
            ctx.fillRect(element.x - 5, element.y - 5, textWidth + 10, textHeight + 10);

            // Draw selection border
            ctx.strokeStyle = '#667eea';
            ctx.lineWidth = 3;
            ctx.strokeRect(element.x - 5, element.y - 5, textWidth + 10, textHeight + 10);

            // Draw resize handles
            const handleSize = 8;
            ctx.fillStyle = '#667eea';
            ctx.fillRect(element.x - 5 - handleSize / 2, element.y - 5 - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + textWidth + 5 - handleSize / 2, element.y - 5 - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x - 5 - handleSize / 2, element.y + textHeight + 5 - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(element.x + textWidth + 5 - handleSize / 2, element.y + textHeight + 5 - handleSize / 2, handleSize, handleSize);
          }

          // Draw text
          ctx.fillStyle = textColor;
          ctx.textAlign = textAlign;
          ctx.textBaseline = 'top';

          let textX = element.x;
          if (textAlign === 'center') {
            textX = element.x + textWidth / 2;
          } else if (textAlign === 'right') {
            textX = element.x + textWidth;
          }

          ctx.fillText(element.text, textX, element.y);

          // Draw underline
          if (underline) {
            ctx.strokeStyle = textColor;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(element.x, element.y + textHeight - 4);
            ctx.lineTo(element.x + textWidth, element.y + textHeight - 4);
            ctx.stroke();
          }

          // Draw strikethrough
          if (strikethrough) {
            ctx.strokeStyle = textColor;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(element.x, element.y + textHeight / 2);
            ctx.lineTo(element.x + textWidth, element.y + textHeight / 2);
            ctx.stroke();
          }
        }
        break;

      case 'comment':
        if (element.x !== undefined && element.y !== undefined) {
          const commentSize = 32; // Size of comment bubble
          const isSelected = element.id === selectedElement;

          // Get user initials (default to 'U' if not set)
          const userInitials = element.userInitials || 'U';

          // Check if comment has replies (show indicator)
          const hasReplies = element.comments && element.comments.length > 0;

          // Draw comment circle with shadow
          ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
          ctx.shadowBlur = 8;
          ctx.shadowOffsetX = 2;
          ctx.shadowOffsetY = 2;

          ctx.beginPath();
          ctx.arc(element.x + commentSize / 2, element.y + commentSize / 2, commentSize / 2, 0, 2 * Math.PI);
          ctx.fillStyle = element.backgroundColor || '#667eea';
          ctx.fill();

          // Reset shadow
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;

          // Draw border (thicker if selected)
          ctx.strokeStyle = isSelected ? '#ffffff' : element.color || '#5568d3';
          ctx.lineWidth = isSelected ? 3 : 2;
          ctx.stroke();

          // Draw user initials
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 14px Inter, system-ui, -apple-system, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(userInitials, element.x + commentSize / 2, element.y + commentSize / 2);

          // Draw reply count badge if there are replies
          if (hasReplies) {
            const replyCount = element.comments!.length;
            const badgeSize = 16;
            const badgeX = element.x + commentSize - badgeSize / 2;
            const badgeY = element.y - badgeSize / 2;

            // Draw badge circle
            ctx.beginPath();
            ctx.arc(badgeX, badgeY, badgeSize / 2, 0, 2 * Math.PI);
            ctx.fillStyle = '#ff6b6b';
            ctx.fill();
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 2;
            ctx.stroke();

            // Draw reply count
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 10px Inter, system-ui, -apple-system, sans-serif';
            ctx.fillText(replyCount.toString(), badgeX, badgeY);
          }

          // Draw selection ring if selected
          if (isSelected) {
            ctx.beginPath();
            ctx.arc(element.x + commentSize / 2, element.y + commentSize / 2, commentSize / 2 + 4, 0, 2 * Math.PI);
            ctx.strokeStyle = '#667eea';
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 5]);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        }
        break;
    }
  };

  // Helper to parse HTML and extract formatted text segments
  const parseHTMLText = (html: string): Array<{ text: string; bold: boolean; italic: boolean; underline: boolean; strikethrough: boolean }> => {
    const segments: Array<{ text: string; bold: boolean; italic: boolean; underline: boolean; strikethrough: boolean }> = [];

    // Simple HTML parser - handles <b>, <strong>, <i>, <em>, <u>, <strike>, <s>
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const parseNode = (node: Node, parentFormatting: { bold: boolean; italic: boolean; underline: boolean; strikethrough: boolean }) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        if (text) {
          segments.push({
            text,
            ...parentFormatting
          });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const tagName = element.tagName.toLowerCase();

        const formatting = { ...parentFormatting };
        if (tagName === 'strong' || tagName === 'b') formatting.bold = true;
        if (tagName === 'em' || tagName === 'i') formatting.italic = true;
        if (tagName === 'u') formatting.underline = true;
        if (tagName === 'strike' || tagName === 's') formatting.strikethrough = true;
        if (tagName === 'br') {
          segments.push({ text: '\n', ...formatting });
          return;
        }
        if (tagName === 'div' && segments.length > 0) {
          segments.push({ text: '\n', ...formatting });
        }

        element.childNodes.forEach(child => parseNode(child, formatting));

        // Add newline after div or p
        if ((tagName === 'div' || tagName === 'p') && element.nextSibling) {
          segments.push({ text: '\n', ...formatting });
        }
      }
    };

    tempDiv.childNodes.forEach(node => parseNode(node, { bold: false, italic: false, underline: false, strikethrough: false }));

    return segments;
  };

  // Helper to render formatted text with wrapping
  const renderFormattedText = (
    ctx: CanvasRenderingContext2D,
    segments: Array<{ text: string; bold: boolean; italic: boolean; underline: boolean; strikethrough: boolean }>,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number,
    fontSize: number,
    fontFamily: string,
    textColor: string,
    textAlign: 'left' | 'center' | 'right',
    maxHeight?: number
  ) => {
    let currentY = y;
    let currentLine: Array<{ text: string; bold: boolean; italic: boolean; underline: boolean; strikethrough: boolean; width: number }> = [];
    let currentLineWidth = 0;

    const drawLine = () => {
      if (currentLine.length === 0) return;
      if (maxHeight && currentY + lineHeight > y + maxHeight) return;

      let lineX = x;
      if (textAlign === 'center') {
        lineX = x + (maxWidth - currentLineWidth) / 2;
      } else if (textAlign === 'right') {
        lineX = x + maxWidth - currentLineWidth;
      }

      currentLine.forEach(segment => {
        const fontStyle = segment.italic ? 'italic' : 'normal';
        const fontWeight = segment.bold ? 'bold' : 'normal';
        ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
        ctx.fillStyle = textColor;
        ctx.fillText(segment.text, lineX, currentY);

        // Draw underline
        if (segment.underline) {
          ctx.strokeStyle = textColor;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(lineX, currentY + lineHeight - 4);
          ctx.lineTo(lineX + segment.width, currentY + lineHeight - 4);
          ctx.stroke();
        }

        // Draw strikethrough
        if (segment.strikethrough) {
          ctx.strokeStyle = textColor;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(lineX, currentY + lineHeight / 2);
          ctx.lineTo(lineX + segment.width, currentY + lineHeight / 2);
          ctx.stroke();
        }

        lineX += segment.width;
      });

      currentLine = [];
      currentLineWidth = 0;
      currentY += lineHeight;
    };

    segments.forEach(segment => {
      // Handle newlines
      if (segment.text === '\n') {
        drawLine();
        currentY += lineHeight;
        return;
      }

      const words = segment.text.split(' ');
      words.forEach((word, index) => {
        if (index > 0) word = ' ' + word;

        const fontStyle = segment.italic ? 'italic' : 'normal';
        const fontWeight = segment.bold ? 'bold' : 'normal';
        ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
        const wordWidth = ctx.measureText(word).width;

        if (currentLineWidth + wordWidth > maxWidth && currentLineWidth > 0) {
          drawLine();
        }

        currentLine.push({
          text: word,
          bold: segment.bold,
          italic: segment.italic,
          underline: segment.underline,
          strikethrough: segment.strikethrough,
          width: wordWidth
        });
        currentLineWidth += wordWidth;
      });
    });

    drawLine();
  };

  // Helper to wrap text
  const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number,
    textAlign: 'left' | 'center' | 'right' = 'left',
    maxHeight?: number,
    underline: boolean = false,
    strikethrough: boolean = false
  ) => {
    let currentY = y;
    const lines: string[] = [];

    // First split by newlines to preserve user's line breaks
    const paragraphs = text.split('\n');

    // Then wrap each paragraph
    for (const paragraph of paragraphs) {
      if (paragraph === '') {
        // Empty line - preserve it
        lines.push('');
        continue;
      }

      const words = paragraph.split(' ');
      let line = '';

      for (let i = 0; i < words.length; i++) {
        const testLine = line + words[i] + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && i > 0) {
          lines.push(line);
          line = words[i] + ' ';
        } else {
          line = testLine;
        }
      }
      if (line.trim()) {
        lines.push(line);
      }
    }

    // Draw lines with alignment
    const originalTextAlign = ctx.textAlign;
    ctx.textAlign = textAlign;

    for (let i = 0; i < lines.length; i++) {
      if (maxHeight && currentY + lineHeight > y + maxHeight) break;

      let lineX = x;
      if (textAlign === 'center') {
        lineX = x + maxWidth / 2;
      } else if (textAlign === 'right') {
        lineX = x + maxWidth;
      }

      const trimmedLine = lines[i].trim();

      // Only draw if line is not empty
      if (trimmedLine) {
        ctx.fillText(trimmedLine, lineX, currentY);

        // Draw underline
        if (underline) {
          const textMetrics = ctx.measureText(trimmedLine);
          const lineWidth = textMetrics.width;
          let underlineX = lineX;

          if (textAlign === 'center') {
            underlineX = lineX - lineWidth / 2;
          } else if (textAlign === 'right') {
            underlineX = lineX - lineWidth;
          }

          ctx.strokeStyle = ctx.fillStyle;
          ctx.lineWidth = 1;
          const underlineY = currentY + lineHeight - 4;
          ctx.beginPath();
          ctx.moveTo(underlineX, underlineY);
          ctx.lineTo(underlineX + lineWidth, underlineY);
          ctx.stroke();
        }

        // Draw strikethrough
        if (strikethrough) {
          const textMetrics = ctx.measureText(trimmedLine);
          const lineWidth = textMetrics.width;
          let strikeX = lineX;

          if (textAlign === 'center') {
            strikeX = lineX - lineWidth / 2;
          } else if (textAlign === 'right') {
            strikeX = lineX - lineWidth;
          }

          ctx.strokeStyle = ctx.fillStyle;
          ctx.lineWidth = 1;
          const strikeY = currentY + lineHeight / 2;
          ctx.beginPath();
          ctx.moveTo(strikeX, strikeY);
          ctx.lineTo(strikeX + lineWidth, strikeY);
          ctx.stroke();
        }
      }

      currentY += lineHeight;
    }

    ctx.textAlign = originalTextAlign;
  };

  // Handle mouse down
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const point = getMousePos(e);

    // Check if clicking on a comment first (comments are clickable in any mode)
    const clickedElement = findElementAtPoint(point);
    if (clickedElement && clickedElement.type === 'comment') {
      setSelectedElement(clickedElement.id);
      if (onSelectElement) {
        onSelectElement(clickedElement);
      }
      return;
    }

    // Check for pan mode - use screen coordinates for smooth panning
    if (currentTool === 'pan' || e.button === 1 || (e.button === 0 && e.ctrlKey)) {
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY }); // Use screen coordinates
      setCursorStyle('grabbing');
      return;
    }

    // Handle eraser tool
    if (currentTool === 'eraser') {
      setIsErasing(true);
      // Check for elements at this point and delete them
      if (clickedElement && (clickedElement.type === 'line' || clickedElement.type === 'pen' || clickedElement.type === 'arrow')) {
        if (onDeleteElement) {
          onDeleteElement(clickedElement.id);
        }
      }
      return;
    }

    // Check for select mode or draggable elements
    if (currentTool === 'select' || currentTool === 'text' || currentTool === 'sticky') {
      // FIRST check if clicking ON an element
      const clickedElement = findElementAtPoint(point);

      // ONLY check connection points if NOT clicking on any element (i.e., clicking in empty space)
      // This ensures dragging has priority over connection drawing
      if (!clickedElement) {
        const selectedStickyNote = elements.find(el => el.id === selectedElement && el.type === 'sticky');
        if (selectedStickyNote) {
          const connectionPoints = getConnectionPoints(selectedStickyNote);
          const dotRadius = 6; // Match visual dot size exactly

          for (const [key, cp] of Object.entries(connectionPoints)) {
            const distance = Math.sqrt(Math.pow(point.x - cp.x, 2) + Math.pow(point.y - cp.y, 2));
            // Check if click is within the visible dot area
            if (distance <= dotRadius) {
              setDrawingConnection(true);
              setConnectionStart({ element: selectedStickyNote, point: cp, pointType: key });
              setConnectionPreview(cp);
              return;
            }
          }
        }
      }

      const isShape = ['rectangle', 'circle', 'ellipse', 'triangle', 'diamond', 'star', 'hexagon', 'frame'].includes(clickedElement?.type || '');
      const isDrawing = clickedElement?.type === 'line' || clickedElement?.type === 'pen';

      if (clickedElement && (clickedElement.type === 'text' || clickedElement.type === 'sticky' || clickedElement.type === 'comment' || clickedElement.type === 'arrow' || isShape || isDrawing)) {
        // Handle comment click - open popup instead of dragging
        if (clickedElement.type === 'comment') {
          setSelectedElement(clickedElement.id);
          if (onSelectElement) {
            onSelectElement(clickedElement);
          }
          return;
        }

        // Handle arrow, line, and pen selection and dragging
        if (clickedElement.type === 'arrow' || isDrawing) {
          setSelectedElement(clickedElement.id);
          if (onSelectElement) {
            onSelectElement(clickedElement);
          }

          // Set up dragging for arrow/line/pen by using first point as reference
          if (clickedElement.points && clickedElement.points.length > 0) {
            setDraggedElement(clickedElement.id);
            setDragOffset({
              x: point.x - clickedElement.points[0].x,
              y: point.y - clickedElement.points[0].y,
            });
            // Notify parent that dragging has started
            if (onDragStateChange) {
              onDragStateChange(true);
            }
          }
          return;
        }

        // Check if clicking on resize handle (for sticky notes and shapes)
        const handle = getResizeHandle(point, clickedElement);
        if (handle && (clickedElement.type === 'sticky' || isShape)) {
          setResizingElement(clickedElement.id);
          setResizeHandle(handle);
          setResizeStartPoint(point);
          setOriginalSize({
            x: clickedElement.x || 0,
            y: clickedElement.y || 0,
            width: clickedElement.width || 200,
            height: clickedElement.height || 200,
          });
          return;
        }

        // Otherwise start dragging
        setDraggedElement(clickedElement.id);
        setDragOffset({
          x: point.x - (clickedElement.x || 0),
          y: point.y - (clickedElement.y || 0),
        });
        // Notify parent that dragging has started
        if (onDragStateChange) {
          onDragStateChange(true);
        }

        // Set selected element for sticky notes, text, and shapes
        if (clickedElement.type === 'sticky' || clickedElement.type === 'text' || isShape) {
          setSelectedElement(clickedElement.id);
          if (onSelectElement) {
            onSelectElement(clickedElement);
          }
        }
        return;
      } else {
        // Clicked on empty space, deselect
        setSelectedElement(null);
        if (onSelectElement) {
          onSelectElement(null);
        }
      }
    }

    // Handle tool-specific actions
    if (currentTool === 'text') {
      if (onRequestTextInput) {
        onRequestTextInput(point);
      }
      return;
    }

    if (currentTool === 'sticky') {
      if (onRequestStickyNote) {
        onRequestStickyNote(point);
      }
      return;
    }

    // Handle comment placement
    if (currentTool === 'comment') {
      const commentSize = 32;
      // Default user - in a real app, this would come from auth context
      const userName = 'Piyush';
      const userInitials = 'P';

      const newComment: DrawingElement = {
        id: generateId(),
        type: 'comment',
        x: point.x - commentSize / 2,
        y: point.y - commentSize / 2,
        width: commentSize,
        height: commentSize,
        color: '#5568d3',
        backgroundColor: '#667eea',
        strokeWidth: 2,
        text: '', // Empty by default, can be edited via properties toolbar
        userName: userName,
        userInitials: userInitials,
        comments: [], // Empty thread initially
        timestamp: Date.now(),
      };

      onAddElement(newComment);

      // Don't auto-select - let user click to open popup
      return;
    }

    // Handle drawing
    setIsDrawing(true);
    setStartPoint(point);

    if (currentTool === 'pen') {
      setCurrentElement({
        id: generateId(),
        type: 'pen',
        points: [point],
        color: currentColor,
        strokeWidth: currentStrokeWidth,
      });
    }
  };

  // Handle mouse move
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const point = getMousePos(e);

    // Handle panning - use screen coordinates for smooth movement
    if (isPanning && lastPanPoint) {
      const dx = e.clientX - lastPanPoint.x;
      const dy = e.clientY - lastPanPoint.y;
      onPanChange({ x: pan.x + dx, y: pan.y + dy });
      setLastPanPoint({ x: e.clientX, y: e.clientY }); // Update with screen coordinates
      return;
    }

    // Handle eraser dragging
    if (isErasing) {
      // Check for elements at this point and delete them
      const hoveredElement = findElementAtPoint(point);
      if (hoveredElement && (hoveredElement.type === 'line' || hoveredElement.type === 'pen' || hoveredElement.type === 'arrow')) {
        if (onDeleteElement) {
          onDeleteElement(hoveredElement.id);
        }
      }
      return;
    }

    // Handle drawing connection
    if (drawingConnection && connectionStart) {
      // Check if hovering over any sticky note's connection points
      let foundConnectionPoint = false;
      for (const element of elements) {
        if (element.type === 'sticky' && element.id !== connectionStart.element.id) {
          const connectionPointType = getConnectionPoint(point, element);
          if (connectionPointType) {
            const connectionPoints = getConnectionPoints(element);
            const targetPoint = connectionPoints[connectionPointType];
            setConnectionPreview(targetPoint);
            setHoveredConnectionPoint({ elementId: element.id, pointType: connectionPointType });
            foundConnectionPoint = true;
            break;
          }
        }
      }

      if (!foundConnectionPoint) {
        setConnectionPreview(point);
        setHoveredConnectionPoint(null);
      }
      return;
    }

    // Check for hover over connection points
    const currentSelectedElement = elements.find(el => el.id === selectedElement);
    if (currentSelectedElement && currentSelectedElement.type === 'sticky' && !resizingElement && !dragOffset) {
      const connectionPointType = getConnectionPoint(point, currentSelectedElement);
      if (connectionPointType) {
        setHoveredConnectionPoint({ elementId: currentSelectedElement.id, pointType: connectionPointType });
        setCursorStyle('crosshair');
        return;
      } else {
        setHoveredConnectionPoint(null);
      }
    } else if (!resizingElement && !dragOffset) {
      setHoveredConnectionPoint(null);
    }

    // Handle resizing elements
    if (resizingElement && resizeHandle && resizeStartPoint && originalSize) {
      const dx = point.x - resizeStartPoint.x;
      const dy = point.y - resizeStartPoint.y;
      const minSize = 50;

      let newX = originalSize.x;
      let newY = originalSize.y;
      let newWidth = originalSize.width;
      let newHeight = originalSize.height;

      // Calculate new dimensions based on handle
      if (resizeHandle.includes('e')) {
        newWidth = Math.max(minSize, originalSize.width + dx);
      }
      if (resizeHandle.includes('w')) {
        newWidth = Math.max(minSize, originalSize.width - dx);
        newX = originalSize.x + (originalSize.width - newWidth);
      }
      if (resizeHandle.includes('s')) {
        newHeight = Math.max(minSize, originalSize.height + dy);
      }
      if (resizeHandle.includes('n')) {
        newHeight = Math.max(minSize, originalSize.height - dy);
        newY = originalSize.y + (originalSize.height - newHeight);
      }

      onUpdateElement(resizingElement, {
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight,
      });
      return;
    }

    // Handle dragging elements
    if (draggedElement && dragOffset) {
      const element = elements.find(el => el.id === draggedElement);

      if (element) {
        // Check if it's a point-based element (line, pen, arrow)
        if ((element.type === 'line' || element.type === 'pen' || element.type === 'arrow') && element.points) {
          // Calculate the delta movement
          const dx = (point.x - dragOffset.x) - element.points[0].x;
          const dy = (point.y - dragOffset.y) - element.points[0].y;

          // Update all points by the same delta
          const newPoints = element.points.map(p => ({
            x: p.x + dx,
            y: p.y + dy
          }));

          onUpdateElement(draggedElement, { points: newPoints });
        } else {
          // For x/y-based elements (text, sticky, shapes)
          const newX = point.x - dragOffset.x;
          const newY = point.y - dragOffset.y;
          onUpdateElement(draggedElement, { x: newX, y: newY });
        }
      }

      setCursorStyle('move');
      return;
    }

    // Update cursor based on hover state
    if (currentTool === 'pan') {
      setCursorStyle('grab');
      return;
    }

    if (currentTool === 'select' || currentTool === 'sticky' || currentTool === 'text') {
      const hoveredElement = findElementAtPoint(point);
      const isHoveredShape = hoveredElement && ['rectangle', 'circle', 'ellipse', 'triangle', 'diamond', 'star', 'hexagon', 'frame'].includes(hoveredElement.type);
      const isHoveredDrawing = hoveredElement && (hoveredElement.type === 'line' || hoveredElement.type === 'pen' || hoveredElement.type === 'arrow');

      if (hoveredElement && (hoveredElement.type === 'sticky' || isHoveredShape)) {
        const handle = getResizeHandle(point, hoveredElement);
        if (handle) {
          setCursorStyle(getCursorForHandle(handle));
          return;
        }
        setCursorStyle('move');
        return;
      }
      if (hoveredElement && (hoveredElement.type === 'text' || isHoveredDrawing)) {
        setCursorStyle('move');
        return;
      }
    }
    setCursorStyle('default');

    if (!isDrawing || !startPoint) return;

    // Handle drawing
    if (currentTool === 'pen' && currentElement) {
      setCurrentElement({
        ...currentElement,
        points: [...(currentElement.points || []), point],
      });
    } else if (['rectangle', 'circle', 'ellipse', 'triangle', 'diamond', 'star', 'hexagon', 'frame'].includes(currentTool)) {
      setCurrentElement({
        id: generateId(),
        type: currentTool as Tool,
        x: Math.min(startPoint.x, point.x),
        y: Math.min(startPoint.y, point.y),
        width: Math.abs(point.x - startPoint.x),
        height: Math.abs(point.y - startPoint.y),
        color: currentColor,
        strokeWidth: currentStrokeWidth,
        text: currentTool === 'frame' ? 'Frame' : undefined,
      });
    } else if (currentTool === 'line') {
      setCurrentElement({
        id: generateId(),
        type: 'line',
        points: [startPoint, point],
        color: currentColor,
        strokeWidth: currentStrokeWidth,
      });
    } else if (currentTool === 'arrow') {
      setCurrentElement({
        id: generateId(),
        type: 'arrow',
        points: [startPoint, point],
        color: currentColor,
        strokeWidth: currentStrokeWidth,
      });
    }
  };

  // Handle mouse up
  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isPanning) {
      setIsPanning(false);
      setLastPanPoint(null);
      setCursorStyle(currentTool === 'pan' ? 'grab' : 'default');
      return;
    }

    // Handle eraser stop
    if (isErasing) {
      setIsErasing(false);
      return;
    }

    // Handle completing connection
    if (drawingConnection && connectionStart && connectionPreview) {
      const point = getMousePos(e);

      // First check if hovering over a connection point (priority)
      let targetElement: DrawingElement | null = null;
      let closestPoint: Point | null = null;
      let endPointType: string | null = null;

      for (const element of elements) {
        if (element.type === 'sticky' && element.id !== connectionStart.element.id) {
          const connectionPointType = getConnectionPoint(point, element);
          if (connectionPointType) {
            const connectionPoints = getConnectionPoints(element);
            closestPoint = connectionPoints[connectionPointType];
            endPointType = connectionPointType;
            targetElement = element;
            break;
          }
        }
      }

      // If not hovering over a connection point, find the element at point
      if (!targetElement) {
        targetElement = findElementAtPoint(point);
      }

      // If we found a target sticky note (and it's not the source)
      if (targetElement && targetElement.type === 'sticky' && targetElement.id !== connectionStart.element.id) {
        // If we don't have a closest point yet, find one
        if (!closestPoint || !endPointType) {
          const targetConnectionPoints = getConnectionPoints(targetElement);
          let minDistance = Infinity;

          Object.entries(targetConnectionPoints).forEach(([key, cp]) => {
            const distance = Math.sqrt(Math.pow(point.x - cp.x, 2) + Math.pow(point.y - cp.y, 2));
            if (distance < minDistance) {
              minDistance = distance;
              closestPoint = cp;
              endPointType = key;
            }
          });
        }

        if (closestPoint && endPointType) {
          // Create arrow connection element with stored connection point types
          const arrowElement: DrawingElement = {
            id: generateId(),
            type: 'arrow',
            points: [connectionStart.point, closestPoint],
            color: '#667eea',
            strokeWidth: 2,
            lineType: 'orthogonal',
            startElementId: connectionStart.element.id,
            endElementId: targetElement.id,
            startConnectionPoint: connectionStart.pointType,
            endConnectionPoint: endPointType,
          };
          onAddElement(arrowElement);
        }
      }

      setDrawingConnection(false);
      setConnectionStart(null);
      setConnectionPreview(null);
      setHoveredConnectionPoint(null);
      return;
    }

    if (resizingElement) {
      setResizingElement(null);
      setResizeHandle(null);
      setResizeStartPoint(null);
      setOriginalSize(null);
      return;
    }

    if (draggedElement) {
      setDraggedElement(null);
      setDragOffset(null);
      // Notify parent that dragging has stopped
      if (onDragStateChange) {
        onDragStateChange(false);
      }
      return;
    }

    if (currentElement) {
      onAddElement(currentElement);
      setCurrentElement(null);
    }

    setIsDrawing(false);
    setStartPoint(null);
  };

  // Update canvas size
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const updateSize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      drawElements();
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [drawElements]);

  // Redraw when elements change
  useEffect(() => {
    drawElements();
  }, [drawElements]);

  // Handle double-click for editing sticky notes
  const handleDoubleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const point = getMousePos(e);
    const clickedElement = findElementAtPoint(point);

    if (clickedElement && clickedElement.type === 'sticky' && onEditStickyNote) {
      onEditStickyNote(clickedElement);
    }
  };

  return (
    <canvas
      ref={canvasRef}
      className={`whiteboard-canvas ${draggedElement ? 'dragging' : ''} ${currentTool === 'eraser' ? 'eraser' : ''}`}
      style={{ cursor: cursorStyle }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onDoubleClick={handleDoubleClick}
    />
  );
};

export default WhiteboardCanvas;
